import * as t from 'io-ts';

import {ExtendedError} from '../../../libs/shared/helpers/src/types/customErrors';
import {reportToBugsnag} from '../../../srcCommon/services/bugsnag/bugsnag';
import {makeApiType, makeEnumRuntimeType, RTagIdRef} from './apiTypes';
import {parseConversationPatch, RConversationPatch} from './conversationPatchModel';

/*
 * Runtime type.
 */

export enum JobTypesEnum {
  MASS_UPDATE_INBOX = 'mass_update_inbox',
  SELECT_ALL = 'true_select_all',
  BACKFILL_CUSTOM_VIEW = 'backfill_custom_view',
  IMPORT_GMAIL_CHANNEL = 'import_gmail_channel',
  IMPORT_OFFICE365_CHANNEL = 'import_office365_channel',
  IMPORT_GOOGLE_CALENDAR_EVENTS = 'google_calendar_event_import',
  IMPORT_OFFICE365_CALENDAR_EVENTS = 'office365_calendar_event_import',
  IMPORT_ACCOUNTS_CSV = 'upload_csv_accounts',
  IMPORT_CONTACTS_CSV = 'upload_csv_contacts',
  EXPORT_CONTACTS_CSV = 'export_cards',
  ACCOUNT_DOMAIN_ASSOCIATION = 'account_domain_association',
  SYNC_ACCOUNTS = 'sync_accounts',
  SYNC_CARDS_FROM_LIST = 'sync_cards_from_list',
  SYNC_CARDS_FOR_ACCOUNTS = 'sync_cards_for_accounts',
}

export enum JobShapesEnum {
  INBOX = 'INBOX',
  INBOXES = 'INBOXES',
  SELECT_ALL = 'SELECT_ALL',
  CHANNEL = 'CHANNEL',
  CALENDAR_VERSION = 'CALENDAR_VERSION',
  TAG = 'TAG',
  ACCOUNTS = 'ACCOUNTS',
  IMPORT_CONTACTS_CSV = 'IMPORT_CONTACTS_CSV',
  EXPORT_CONTACTS_CSV = 'EXPORT_CONTACTS_CSV',
  ACCOUNT_DOMAIN_ASSOCIATION = 'ACCOUNT_DOMAIN_ASSOCIATION',
  SYNC = 'SYNC',
}

export enum JobStatusesEnum {
  INIT = 'init',
  WAITING = 'wait',
  RUNNING = 'run',
  FAILED = 'failed',
  DONE = 'done',
}

export enum ExportCardsJobOptionTypesEnum {
  COMPANY = 'company',
  PRIVATE = 'private',
  SHARED = 'shared',
}

const RJobType = makeEnumRuntimeType(JobTypesEnum);
const RJobStatus = makeEnumRuntimeType(JobStatusesEnum);
const RExportContactsTypes = makeEnumRuntimeType(ExportCardsJobOptionTypesEnum);

const RJobBase = t.type({
  id: t.Integer,
  status: RJobStatus,
  stopped: t.boolean,
  stepCount: t.Integer,
  completedStepCount: t.Integer,
  requesterId: t.union([t.undefined, t.Integer]),
});

const RInboxJob = t.intersection([
  RJobBase,
  t.type({
    shape: t.literal(JobShapesEnum.INBOX),
    type: t.literal(JobTypesEnum.MASS_UPDATE_INBOX),
    inboxId: t.Integer,
  }),
]);

const RInboxesJob = t.intersection([
  RJobBase,
  t.type({
    shape: t.literal(JobShapesEnum.INBOXES),
    type: t.literal(JobTypesEnum.SELECT_ALL),
    inboxIds: t.readonlyArray(t.Integer),
  }),
]);

const RChannelJob = t.intersection([
  RJobBase,
  t.type({
    shape: t.literal(JobShapesEnum.CHANNEL),
    type: t.union([
      t.literal(JobTypesEnum.IMPORT_GMAIL_CHANNEL),
      t.literal(JobTypesEnum.IMPORT_OFFICE365_CHANNEL),
    ]),
    channelId: t.Integer,
  }),
]);

const RSelectAllJob = t.intersection([
  RJobBase,
  t.type({
    shape: t.literal(JobShapesEnum.SELECT_ALL),
    type: t.literal(JobTypesEnum.SELECT_ALL),
    patch: RConversationPatch,
  }),
]);

const RCalendarVersionJob = t.intersection([
  RJobBase,
  t.type({
    shape: t.literal(JobShapesEnum.CALENDAR_VERSION),
    type: t.union([
      t.literal(JobTypesEnum.IMPORT_GOOGLE_CALENDAR_EVENTS),
      t.literal(JobTypesEnum.IMPORT_OFFICE365_CALENDAR_EVENTS),
    ]),
    calendarVersionId: t.Integer,
  }),
]);

const RTagJob = t.intersection([
  RJobBase,
  t.type({
    shape: t.literal(JobShapesEnum.TAG),
    type: t.literal(JobTypesEnum.BACKFILL_CUSTOM_VIEW),
    tagId: RTagIdRef,
  }),
]);

const RAccountsJob = t.intersection([
  RJobBase,
  t.type({
    shape: t.literal(JobShapesEnum.ACCOUNTS),
    type: t.literal(JobTypesEnum.IMPORT_ACCOUNTS_CSV),
  }),
]);

const RImportContactCsvJob = t.intersection([
  RJobBase,
  t.type({
    shape: t.literal(JobShapesEnum.IMPORT_CONTACTS_CSV),
    type: t.literal(JobTypesEnum.IMPORT_CONTACTS_CSV),
  }),
]);

const RExportContactsCsvJob = t.intersection([
  RJobBase,
  t.type({
    shape: t.literal(JobShapesEnum.EXPORT_CONTACTS_CSV),
    type: t.literal(JobTypesEnum.EXPORT_CONTACTS_CSV),
    exportType: RExportContactsTypes,
  }),
]);

const RAccountDomainAssociationJob = t.intersection([
  RJobBase,
  t.type({
    shape: t.literal(JobShapesEnum.ACCOUNT_DOMAIN_ASSOCIATION),
    type: t.literal(JobTypesEnum.ACCOUNT_DOMAIN_ASSOCIATION),
    account_id: t.number,
  }),
]);

const RSyncJob = t.intersection([
  RJobBase,
  t.type({
    shape: t.literal(JobShapesEnum.SYNC),
    type: t.union([
      t.literal(JobTypesEnum.SYNC_ACCOUNTS),
      t.literal(JobTypesEnum.SYNC_CARDS_FROM_LIST),
      t.literal(JobTypesEnum.SYNC_CARDS_FOR_ACCOUNTS),
    ]),
    sync_id: t.number,
    createdAt: t.number,
  }),
  t.partial({
    metadata: t.type({
      syncFailed: t.number,
      syncSucceeded: t.number,
      syncWarnings: t.number,
    }),
  }),
]);

const RJobDirty = t.taggedUnion(
  'shape',
  [
    RInboxJob,
    RInboxesJob,
    RSelectAllJob,
    RChannelJob,
    RCalendarVersionJob,
    RTagJob,
    RAccountsJob,
    RImportContactCsvJob,
    RExportContactsCsvJob,
    RAccountDomainAssociationJob,
    RSyncJob,
  ],
  'job',
);

/*
 * Type.
 */

export interface InboxJobModel extends Readonly<t.TypeOf<typeof RInboxJob>> {}
export interface InboxesJobModel extends Readonly<t.TypeOf<typeof RInboxesJob>> {}
export interface ChannelJobModel extends Readonly<t.TypeOf<typeof RChannelJob>> {}
export interface SelectAllJobModel extends Readonly<t.TypeOf<typeof RSelectAllJob>> {}
export interface CalendarVersionJobModel extends Readonly<t.TypeOf<typeof RCalendarVersionJob>> {}
export interface TagJobModel extends Readonly<t.TypeOf<typeof RTagJob>> {}
export interface AccountsJobModel extends Readonly<t.TypeOf<typeof RAccountsJob>> {}
export interface ImportContactsCsvJobModel extends Readonly<t.TypeOf<typeof RImportContactCsvJob>> {}
export interface ExportContactsCsvJobModel extends Readonly<t.TypeOf<typeof RExportContactsCsvJob>> {}
export interface AccountDomainAssociationJobModel
  extends Readonly<t.TypeOf<typeof RAccountDomainAssociationJob>> {}
export interface SyncJobModel extends Readonly<t.TypeOf<typeof RSyncJob>> {}

export type JobModel =
  | ChannelJobModel
  | SelectAllJobModel
  | InboxJobModel
  | InboxesJobModel
  | CalendarVersionJobModel
  | TagJobModel
  | AccountsJobModel
  | ImportContactsCsvJobModel
  | ExportContactsCsvJobModel
  | AccountDomainAssociationJobModel
  | SyncJobModel;

export const RJob = t.clean<JobModel>(RJobDirty as any);

/*
 * Type guards.
 */

export const isJobType = RJobType.is;

/*
 * IO functions.
 */

export function parseJob(src: any): JobModel {
  const {type, resources} = src;

  if (!RJobType.is(type) || !t.readonlyArray(t.any).is(resources)) {
    throw new Error('Invalid job.');
  }

  const baseJob = {
    id: src.id,
    status: src.status,
    stopped: src.stopped,
    stepCount: src.progress.total,
    completedStepCount: src.progress.done,
    // eslint-disable-next-line camelcase
    requesterId: src.parameters?.requester_id || undefined,
  };

  switch (type) {
    case JobTypesEnum.MASS_UPDATE_INBOX: {
      const inboxResource = resources.find((r) => r.resource_type === 'inbox');
      if (!inboxResource) {
        throw new Error('Invalid job.');
      }

      return {
        ...baseJob,
        shape: JobShapesEnum.INBOX,
        type,
        inboxId: inboxResource.resource_id,
      };
    }

    case JobTypesEnum.SELECT_ALL: {
      return {
        ...baseJob,
        shape: JobShapesEnum.SELECT_ALL,
        type,
        patch: parseConversationPatch(src.parameters.patch_data),
      };
    }

    case JobTypesEnum.IMPORT_GMAIL_CHANNEL:
    case JobTypesEnum.IMPORT_OFFICE365_CHANNEL: {
      const channelResource = resources.find((r) => r.resource_type === 'channel');
      if (!channelResource) {
        throw new Error('Invalid job.');
      }

      return {
        ...baseJob,
        shape: JobShapesEnum.CHANNEL,
        type,
        channelId: channelResource.resource_id,
      };
    }

    case JobTypesEnum.IMPORT_GOOGLE_CALENDAR_EVENTS:
    case JobTypesEnum.IMPORT_OFFICE365_CALENDAR_EVENTS: {
      const calendarVersionResource = resources.find((r) => r.resource_type === 'calendarVersion');
      if (!calendarVersionResource) {
        throw new Error('Invalid job.');
      }

      return {
        ...baseJob,
        shape: JobShapesEnum.CALENDAR_VERSION,
        type,
        calendarVersionId: calendarVersionResource.resource_id,
      };
    }

    case JobTypesEnum.BACKFILL_CUSTOM_VIEW: {
      const tagResource = resources.find((r) => r.resource_type === 'tag');
      if (!tagResource) {
        throw new Error('Invalid job.');
      }

      return {
        ...baseJob,
        shape: JobShapesEnum.TAG,
        type,
        tagId: tagResource.resource_id,
      };
    }

    case JobTypesEnum.IMPORT_ACCOUNTS_CSV: {
      return {
        ...baseJob,
        shape: JobShapesEnum.ACCOUNTS,
        type,
      };
    }

    case JobTypesEnum.IMPORT_CONTACTS_CSV: {
      return {
        ...baseJob,
        shape: JobShapesEnum.IMPORT_CONTACTS_CSV,
        type,
      };
    }

    case JobTypesEnum.EXPORT_CONTACTS_CSV: {
      return {
        ...baseJob,
        shape: JobShapesEnum.EXPORT_CONTACTS_CSV,
        type,
        exportType: src.parameters?.export_type,
      };
    }

    case JobTypesEnum.ACCOUNT_DOMAIN_ASSOCIATION: {
      return {
        ...baseJob,
        shape: JobShapesEnum.ACCOUNT_DOMAIN_ASSOCIATION,
        type,
        account_id: src.parameters.account_id,
      };
    }
    case JobTypesEnum.SYNC_CARDS_FROM_LIST:
    case JobTypesEnum.SYNC_CARDS_FOR_ACCOUNTS:
    case JobTypesEnum.SYNC_ACCOUNTS: {
      return {
        ...baseJob,
        createdAt: src.created_at,
        shape: JobShapesEnum.SYNC,
        type,
        sync_id: src.parameters.sync_id,
        metadata: parseSyncJobMetadata(src.batch_status),
      };
    }

    default:
      throw new Error('Invalid job.');
  }
}

export function tryParseJob(src: any) {
  if (!RJobType.is(src.type)) {
    return undefined;
  }

  try {
    return parseJob(src);
  } catch (error) {
    reportToBugsnag(new ExtendedError('Job could not be parsed.', error));
    return undefined;
  }
}

export function parseJobInInbox(src: any): JobModel {
  return parseJob(src.job);
}

function parseSyncJobMetadata(src: any) {
  if (!src) {
    return undefined;
  }
  return {
    syncFailed: src.numFailed || 0,
    syncSucceeded: src.numSucceeded || 0,
    syncWarnings: src.numSucceededWithWarnings || 0,
  };
}

/*
 * API type.
 */

export const ApiJob = makeApiType('job', RJob, parseJob);
export const ApiJobInInbox = makeApiType('jobInInbox', RJob, parseJobInInbox);
